import { useTranslation } from 'react-i18next';
import { useLabel } from '@platform/hooks/useLabel';
import { builderCategories } from './builderCategories';
import type { CustomModulePart } from './CustomModulePart';
import type { IDefinitionCardProps } from './IDefinitionCardProps';
import { setTypeText } from './patchText';
import { TextCommit } from './TextCommit';

/**
 * Картка «Визначення» (прототип v4 pgBuilder): назва (множина) — для головного типу це назва модуля в просторі (PATCH модуля), для
 * дочірнього — назва типу; однина й опис — перевизначення типу мовою інтерфейсу; префікс ID, категорія (як у прототипі — builderCategories)
 * і рівень модуля каталогу — лише для перегляду (їх задає опубліковане визначення). Власний модуль простору (ADR-025, `custom`) змінює все,
 * крім рівня: назву, однину, префікс (великі латинські літери й цифри), категорію й опис — PATCH …/custom-modules/{key}.
 */
export function DefinitionCard({ module, rt, editor, strings, moduleName, categories, onRenameModule, custom }: IDefinitionCardProps) {
  const { t } = useTranslation();
  const label = useLabel();
  const name = rt.main ? moduleName : strings.entity(rt.key, 'name', rt.name);
  const singular = strings.entity(rt.key, 'singular', rt.singular ?? null) || name;
  const description = strings.entity(rt.key, 'description', rt.description ?? null) || strings.tagline(module.module.tagline);
  /** Опублікований текст (рядок пакета без перевизначень простору): збіг із ним прибирає перевизначення. */
  const plain = (part: 'name' | 'singular' | 'description') => strings.entity(rt.key, part, null);
  const edit = (part: 'name' | 'singular' | 'description') => (value: string) =>
    editor.apply((p) => setTypeText(p, part, strings.lang, value, plain(part) || (part === 'description' ? strings.tagline(module.module.tagline) : '')));
  const own = (part: CustomModulePart) => (value: string) => custom?.commit(part, value);
  const field = (key: string, control: JSX.Element, wide = false) => (
    <label className={wide ? 'rb-field rb-field--wide' : 'rb-field'}><span className="rb-field__label">{t(`rtBuilder.${key}`)}</span>{control}</label>
  );
  const off = custom ? !custom.canEdit : !editor.canEdit;
  const category = builderCategories(module.module, categories, label);
  return (
    <section className="rb-card" aria-label={t('rtBuilder.definition')}>
      <div className="rb-card__title">{t('rtBuilder.definition')}</div>
      <div className="rb-grid">
        {field('plural', <TextCommit className="rb-input" value={name} label={t('rtBuilder.plural')} disabled={off}
          onCommit={custom ? own('name') : rt.main ? onRenameModule : edit('name')} />)}
        {field('singular', <TextCommit className="rb-input" value={singular} label={t('rtBuilder.singular')} disabled={off} onCommit={custom ? own('singular') : edit('singular')} />)}
        {field('prefix', custom
          ? <TextCommit className="rb-input rb-input--mono" value={rt.prefix} label={t('rtBuilder.prefix')} disabled={off} onCommit={own('prefix')} />
          : <input className="rb-input rb-input--mono" value={rt.prefix} disabled title={t('rtBuilder.prefixFixed')} readOnly />)}
        {field('category', (
          <select className="rb-select" value={category.value} disabled={!custom || off} title={custom ? undefined : t('rtBuilder.categoryFixed')}
            onChange={(e) => own('category')(e.target.value)}>
            {category.options.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
        ))}
        {field('scope', <input className="rb-input rb-input--muted" value={t('rtBuilder.scopeWs')} disabled readOnly />)}
        {field('description', <TextCommit className="rb-input" value={description} label={t('rtBuilder.description')} disabled={off}
          onCommit={custom ? own('description') : edit('description')} />, true)}
      </div>
    </section>
  );
}
